import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import productData from "../Datas/productData";
import "../css/orderdetail.scss";

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [mockOrder, setMockOrder] = useState(null);

  useEffect(() => {
    // Load orders from localStorage
    const savedOrders = JSON.parse(localStorage.getItem("myOrders") || "[]");
    const currentOrder = savedOrders.find((order) => order.id === orderId);

    if (currentOrder) {
      setMockOrder(currentOrder);
    } else {
      // If order is not found, fallback to a default mock order or redirect
      setMockOrder({
        id: orderId || "ORD20240513001",
        status: "shipped",
        date: "2024-05-13",
        paymentMethod: "Credit Card",
        items: [
          { productId: 101, quantity: 1, size: "5-6Y", price: 499.0 },
          { productId: 102, quantity: 1, size: "5-6Y", price: 559.0 },
        ],
        subtotal: 1058.0,
        shipping: 0,
        total: 1058.0,
        shippingAddress: {
          name: "Guest User",
          phone: "138****8888",
          address: "123 Fashion Street, Milan, Italy 20100",
        },
        trackingNumber: "SF1234567890",
      });
    }
  }, [orderId]);

  if (!mockOrder) {
    return null; // Or a loading spinner
  }

  const orderProducts = mockOrder.items.map((item) => {
    // Some productId in localStorage might be string, productData id is integer
    const product = productData.find((p) => p.id === parseInt(item.productId));
    return product ? { ...product, ...item } : null;
  }).filter(Boolean);

  const getStatusSteps = (status) => {
    const steps = [
      { label: "Order Placed", date: mockOrder.date },
      { label: "Payment Confirmed", date: mockOrder.date },
      { label: "Processing", date: "2024-05-14" },
      { label: "Shipped", date: "2024-05-15" },
      { label: "Delivered", date: null },
    ];

    const statusIndex = {
      pending: 1,
      processing: 2,
      shipped: 3,
      completed: 4,
    };

    return steps.map((step, index) => ({
      ...step,
      completed: index <= statusIndex[status],
      current: index === statusIndex[status],
    }));
  };

  const statusSteps = getStatusSteps(mockOrder.status);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="order-detail-page color-scheme color-scheme--scheme-1">
      <Header />
      <main className="order-detail-page__main">
        <div className="order-detail-container">
          <div className="order-detail-header">
            <Link to="/orders" className="back-link" onClick={toTop}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
              Back to Orders
            </Link>
            <h1 className="order-detail-title">Order #{mockOrder.id}</h1>
          </div>

          <div className="order-progress">
            <div className="progress-steps">
              {statusSteps.map((step, index) => (
                <div key={index} className={`progress-step ${step.completed ? "is-completed" : ""} ${step.current ? "is-current" : ""}`}>
                  <div className="step-indicator">
                    {step.completed && !step.current ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="14" height="14">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="step-info">
                    <span className="step-label">{step.label}</span>
                    {step.date && <span className="step-date">{step.date}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="order-detail-content">
            <div className="order-detail-main">
              <section className="detail-section">
                <h2 className="section-title">Order Items</h2>
                <div className="order-items">
                  {orderProducts.map((product) => (
                    <div key={product.id} className="order-item">
                      <div className="order-item__image">
                        <img src={product.images[0]} alt={product.name} />
                      </div>
                      <div className="order-item__info">
                        <Link to={`/shopDetails/${product.id}`} className="order-item__name" onClick={toTop}>
                          {product.name}
                        </Link>
                        <p className="order-item__meta">
                          Size: {product.size} | Qty: {product.quantity}
                        </p>
                        <p className="order-item__price">€ {Number(product.price).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {mockOrder.trackingNumber && (
                <section className="detail-section">
                  <h2 className="section-title">Shipping Information</h2>
                  <div className="shipping-info">
                    <div className="info-row">
                      <span className="info-label">Tracking Number</span>
                      <span className="info-value">{mockOrder.trackingNumber}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Carrier</span>
                      <span className="info-value">Express Delivery</span>
                    </div>
                    <Link
                      to={`/tracking/${mockOrder.id}`}
                      className="track-btn"
                      onClick={toTop}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="18">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                      </svg>
                      Track Package
                    </Link>
                  </div>
                </section>
              )}
            </div>

            <aside className="order-detail-sidebar">
              <section className="detail-section">
                <h2 className="section-title">Shipping Address</h2>
                {mockOrder.shippingAddress ? (
                  <div className="address-info">
                    <p className="address-name">{mockOrder.shippingAddress.name}</p>
                    <p className="address-phone">{mockOrder.shippingAddress.phone}</p>
                    <p className="address-detail">{mockOrder.shippingAddress.address}</p>
                  </div>
                ) : (
                  <div className="address-info">
                    <p className="address-detail">No shipping address provided.</p>
                  </div>
                )}
              </section>

              <section className="detail-section">
                <h2 className="section-title">Payment Method</h2>
                <p className="payment-method">{mockOrder.paymentMethod || "Credit Card"}</p>
              </section>

              <section className="detail-section">
                <h2 className="section-title">Order Summary</h2>
                <div className="order-summary">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>€ {Number(mockOrder.subtotal || mockOrder.total).toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Shipping</span>
                    <span>{Number(mockOrder.shipping || 0) === 0 ? "Free" : `€ ${Number(mockOrder.shipping).toFixed(2)}`}</span>
                  </div>
                  <div className="summary-row summary-row--total">
                    <span>Total</span>
                    <span>€ {Number(mockOrder.total).toFixed(2)}</span>
                  </div>
                </div>
              </section>

              <div className="order-actions">
                {mockOrder.status === "shipped" && (
                  <Link
                    to={`/aftersales/return/${mockOrder.id}`}
                    className="action-btn action-btn--outline"
                    onClick={toTop}
                  >
                    Request Return
                  </Link>
                )}
                <button type="button" className="action-btn">
                  Contact Support
                </button>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OrderDetail;
